import { LoadingOutlined } from "@ant-design/icons";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import CustomDatePicker from "../../../components/Datepicker/CustomDatePicker";
import { get_language, translate } from "../../../locale";
import { AdminContext } from "../../../providers/AdminContext";
import UserServices from "../../../services/UserServices";
import ChangePasswordBox from "./Views/ChangePasswordBox";
import { EMAIL_TARGETS, PHONE_TARGETS } from "./constants";

const { Option } = Select;
const { Title, Text } = Typography;

const SimpleReactValidatorMessage = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [sendingForm, setSendingForm] = useState(false);
  const { username } = useParams();
  const [values, setValues] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    country_code: "",
    birth: "",
    identity_label: "",
    identity_value: "",
    biological_gender: "",
    phones: [],
    emails: [],
    num_professional_registration: "",
    height: 0,
  });
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const doc_types = [
    {
      id: "",
      label: "Seleccione...",
    },
    {
      id: "dni",
      label: "DNI",
    },
  ];
  const genders = [
    {
      id: "",
      label: "Seleccione...",
    },
    {
      id: "m",
      label: "Masculino",
    },
    {
      id: "f",
      label: "Femenino",
    },
  ];
  const { user } = useContext(AdminContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [extProvider, setExtProvider] = useState(false);
  const [countries, setCountries] = useState([]);

  const [form] = Form.useForm();
  const disabled = () => !simpleValidator.current.allValid();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: SimpleReactValidatorMessage,
      messages: {
        required: "Campo requerido",
      },
      validators: {
        date_older_than: {
          message: translate("Debe ser mayor a 18 años"),
          rule: function (val, params) {
            return (
              moment(val).diff(moment().subtract(params[0], "years"), "hours") <
              0
            );
          },
        },
      },
    })
  );
  useEffect(() => {
    loadProfile();
    loadCountries();
    setIsAdmin(
      user.user.rol === "eira-super-admin" ||
        user.user.rol === "eira-company-admin" ||
        user.user.rol === "eira-company-coordinator"
    );
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.my_contact_info;
        setValues({
          ...data,
          country_code:
            data.nationality?.code === undefined ? "" : data.nationality.code,
          who_user: username,
        });
        setExtProvider(data.user.provider === "cronic");
      }
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    setLoadingCountries(true);
    setError("");
    try {
      const res = await UserServices.getCountries(username);
      if (res.status >= 200 && res.status < 300) {
        setCountries(
          res.data.countries.map((i) => ({ id: i.code, label: i.name }))
        );
      }
      setLoadingCountries(false);
    } catch (e) {
      setError(e.message);
      setLoadingCountries(false);
    }
  };

  const onFinish = async (data) => {
    setSendingForm(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError(translate("Todos los campos son requeridos."));
      return;
    }
    try {
      const newValues = {
        ...values,
        birth: moment(values.birth).format("YYYY-MM-DD"),
      };
      const res = await UserServices.updateProfile(newValues);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(translate("Los datos se actualizaron exitosamente."));
        } else {
          setError(translate("No se pudieron actualizar los datos."));
        }
        setSendingForm(false);
      } else {
        setError(
          translate("Ocurrió un problema procesando los datos del formulario.")
        );
      }
    } catch (e) {
      console.error("onFinish error:", e);
      setError(e.message);
      setSendingForm(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleDatePickerChange = (value) => {
    simpleValidator.current.showMessageFor("birth");
    setValues((v) => ({
      ...v,
      birth: value === null ? "" : moment(value).format("YYYY-MM-DD"),
    }));
  };

  const handleDatePickerBlur = (value) => {
    simpleValidator.current.showMessageFor("birth");
    setValues((v) => ({ ...v, birth: value === null ? "" : value }));
  };

  const handleTarget = (value, index, fieldname, field) => {
    setValues((v) => {
      const newPhone = v[fieldname];
      newPhone[index] = { ...newPhone[index], target: value };
      return {
        ...v,
        [fieldname]: newPhone,
      };
    });
  };

  const handlePhone = (e, index) => {
    const { value } = e.target;

    setValues((v) => {
      const newPhone = v.phones;
      newPhone[index] = { ...newPhone[index], number: value };
      return {
        ...v,
        phones: newPhone,
      };
    });
  };

  const handleEmail = (e, index) => {
    const { value } = e.target;

    setValues((v) => {
      const newEmail = v.emails;
      newEmail[index] = { ...newEmail[index], email: value };
      return {
        ...v,
        emails: newEmail,
      };
    });
  };

  const addRow = (name) => {
    const value =
      name === "phones"
        ? { number: "", target: "" }
        : { email: "", target: "" };
    setValues((v) => ({
      ...v,
      [name]: [...v[name], value],
    }));
    simpleValidator.current.showMessageFor(name);
  };

  const removeRow = (index, name) => {
    const newValue = values[name].filter((i, k) => k !== index);
    setValues((v) => ({ ...v, [name]: newValue }));
    simpleValidator.current.showMessageFor(name);
  };

  return (
    <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 my-10 mx-4 md:mx-10">
      <Row>
        <Col className="gutter-row" span="24">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/admin">{translate("Inicio")}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/admin/buscar-paciente/${username}`}>
                {translate("Resumen individual")}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{translate("Ficha personal")}</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col span="24" className="my-4">
          <Title level={3} className="text-center !text-xl md:!text-3xl">
            {translate("Ficha personal")}
          </Title>
          {error && (
            <Alert
              message={error}
              type="error"
              className="mb-6"
              closable
              onClose={() => setError("")}
            />
          )}
        </Col>
      </Row>
      {!loading && (
        <>
          <Form
            onFinish={onFinish}
            autoComplete="off"
            className="w-full"
            form={form}
            initialValues={values}
          >
            <Row justify="center" gutter={24}>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }}>
                <Form.Item>
                  <Text>{translate("Email Usuario")}</Text>
                  <Input
                    name="username"
                    value={values.user.username}
                    disabled={true}
                  />
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Nombre")}*</Text>
                  <Input
                    placeholder="Nombre"
                    name="first_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    disabled={!isAdmin || extProvider}
                  />
                  {simpleValidator.current.message(
                    "first_name",
                    values.first_name,
                    "required",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo Nombre es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Segundo Nombre")}</Text>
                  <Input
                    placeholder={translate("Segundo Nombre")}
                    name="middle_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.middle_name}
                    disabled={!isAdmin || extProvider}
                  />
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Apellido")}*</Text>
                  <Input
                    placeholder={translate("Apellido")}
                    name="last_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                    disabled={!isAdmin || extProvider}
                  />
                  {simpleValidator.current.message(
                    "last_name",
                    values.last_name,
                    "required",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo Apellido es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Fecha de nacimiento")}*</Text>
                  <CustomDatePicker
                    wrapperClassName="border-custom_primary"
                    onChange={handleDatePickerChange}
                    onClickOutside={handleDatePickerBlur}
                    selected={
                      values.birth ? new Date(moment(values.birth)) : ""
                    }
                    maxDate={new Date(moment().subtract(18, "years"))}
                    locale={get_language}
                    format="YYYY-MM-DD"
                    placeholder={
                      get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
                    }
                    disabled={!isAdmin || extProvider}
                  />
                  {simpleValidator.current.message(
                    "birth",
                    values.birth,
                    "required|date_older_than:18"
                  )}
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Edad (calculada)")}</Text>
                  <Input
                    placeholder={translate("Edad (calculada)")}
                    name="age"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.age}
                    disabled={!isAdmin || extProvider}
                  />
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Sexo")}</Text>
                  <Select
                    name="biological_gender"
                    onChange={(v) =>
                      handleChange({
                        target: { name: "biological_gender", value: v },
                      })
                    }
                    value={values.biological_gender}
                    className="w-full"
                    disabled={!isAdmin || extProvider}
                  >
                    {genders.map((p, k) => (
                      <Option key={p.id} value={p.id}>
                        {p.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Text>{translate("Talla (cm)")}</Text>
                  <Input
                    placeholder={translate("Talla (Altura en cm)")}
                    name="height"
                    value={values.height}
                    disabled={true}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }}>
                <Form.Item>
                  <Text>{translate("País")}*</Text>
                  <Select
                    name="country_code"
                    placeholder="País"
                    onChange={(v) =>
                      handleChange({
                        target: { name: "country_code", value: v },
                      })
                    }
                    value={loadingCountries ? "" : values.country_code}
                    disabled={!isAdmin || extProvider}
                  >
                    {countries.length > 0 &&
                      countries.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.label}
                        </Option>
                      ))}
                    {loadingCountries === true && (
                      <Option value="">{translate("Cargando")}...</Option>
                    )}
                  </Select>
                  {simpleValidator.current.message(
                    "country_code",
                    values.country_code,
                    "required",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo Rol de usuario es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Row gutter={8}>
                  <Col span="8">
                    <Form.Item>
                      <Text>{translate("Documento")}</Text>
                      <Select
                        name="identity_label"
                        onChange={(v) =>
                          handleChange({
                            target: { name: "identity_label", value: v },
                          })
                        }
                        value={values.identity_label}
                        className="w-full"
                        disabled={!isAdmin || extProvider}
                      >
                        {doc_types.map((p) => (
                          <Option key={p.id} value={p.id}>
                            {p.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item>
                      <Text>{translate("Número")}</Text>
                      <Input
                        placeholder={translate("Número")}
                        name="identity_value"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.identity_value}
                        disabled={!isAdmin || extProvider}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div>
                  <Text>{translate("Teléfono")}</Text>
                  <Form.List name="phones">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <div key={field.key} className="relative">
                            <div
                              style={{ width: "calc(100% - 32px)" }}
                              className="flex space-x-2 mb-6"
                            >
                              <Form.Item
                                name={[field.name, "target"]}
                                className="mb-0"
                              >
                                <Select
                                  placeholder="Tipo"
                                  onChange={(e) =>
                                    handleTarget(e, index, "phones", field)
                                  }
                                  disabled={!isAdmin || extProvider}
                                >
                                  {PHONE_TARGETS.map((i) => (
                                    <Option key={i.id} value={i.id}>
                                      {i.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "number"]}
                                className="mb-0 flex-1"
                              >
                                <Input
                                  placeholder="Teléfono"
                                  onChange={(e) => handlePhone(e, index)}
                                  disabled={!isAdmin || extProvider}
                                />
                              </Form.Item>
                            </div>
                            <Button
                              shape="circle"
                              className="absolute right-0 bottom-1 bg-custom_primary text-white border-0 hover:bg-custom_primary hover:text-white focus:bg-custom_primary focus:text-white"
                              size="small"
                              onClick={(e) => {
                                remove(field.name);
                                removeRow(field.name, "phones");
                              }}
                              disabled={!isAdmin || extProvider}
                            >
                              -
                            </Button>
                          </div>
                        ))}
                        {fields.length === 0 && add()}
                        <div>
                          <Button
                            className=" bg-custom_green text-white border-0 hover:bg-custom_green hover:text-white focus:bg-custom_green focus:text-white mb-6"
                            onClick={() => {
                              add();
                              addRow("phones");
                            }}
                            disabled={!isAdmin || extProvider}
                          >
                            Agregar teléfono
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.List>
                </div>
                <div>
                  <Text>{translate("Email")}</Text>

                  <Form.List name="emails">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <div key={field.key} className="relative">
                            <div
                              style={{ width: "calc(100% - 32px)" }}
                              className="flex space-x-2 mb-6"
                            >
                              <Form.Item
                                name={[field.name, "target"]}
                                className="mb-0"
                              >
                                <Select
                                  placeholder="Tipo"
                                  onChange={(e) =>
                                    handleTarget(e, index, "emails", field)
                                  }
                                  disabled={!isAdmin || extProvider}
                                >
                                  {EMAIL_TARGETS.map((i) => (
                                    <Option key={i.id} value={i.id}>
                                      {i.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "email"]}
                                className="mb-0 flex-1"
                              >
                                <Input
                                  placeholder="Email"
                                  onChange={(e) => handleEmail(e, index)}
                                  disabled={!isAdmin || extProvider}
                                />
                              </Form.Item>
                            </div>
                            <Button
                              shape="circle"
                              className="absolute right-0 bottom-1 bg-custom_primary text-white border-0 hover:bg-custom_primary hover:text-white focus:bg-custom_primary focus:text-white"
                              size="small"
                              onClick={(e) => {
                                remove(field.name);
                                removeRow(field.name, "emails");
                              }}
                              disabled={!isAdmin || extProvider}
                            >
                              -
                            </Button>
                          </div>
                        ))}
                        {fields.length === 0 && add()}
                        <div>
                          <Button
                            className=" bg-custom_green text-white border-0 hover:bg-custom_green hover:text-white focus:bg-custom_green focus:text-white mb-6"
                            onClick={() => {
                              add();
                              addRow("emails");
                            }}
                            disabled={!isAdmin || extProvider}
                          >
                            {translate("Agregar email")}
                          </Button>
                        </div>
                      </>
                    )}
                  </Form.List>
                </div>

                {values.user?.role !== undefined &&
                  values.user.rol === "eira-professional" && (
                    <Form.Item>
                      <Text>{translate("Nro. de Matrícula Profesional")}</Text>
                      <Input
                        placeholder="Nro. de Matrícula Profesional"
                        name="num_professional_registration"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.num_professional_registration}
                        disabled={!isAdmin || extProvider}
                      />
                    </Form.Item>
                  )}
              </Col>
            </Row>
            <Row justify="center" gutter={24} className="mb-4 mt-10 md:mt-0">
              <Col>*{translate("Campos requeridos")}</Col>
            </Row>
            <Row justify="center" gutter={24}>
              <Col>
                <Button
                  disabled={
                    disabled() || !isAdmin || extProvider || sendingForm
                  }
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
          <Row justify="center" className="mt-20">
            <Col className="gutter-row" span="12">
              <Card title={translate("Cambiar contraseña")}>
                <Row justify="center" gutter={24}>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
                    <ChangePasswordBox
                      username={values?.user?.username}
                      password={values.password}
                      isAdmin={isAdmin}
                      extProvider={extProvider}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Profile;
